// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import {
  CONTRIBUTION_FREQUENCY_OPTIONS,
  COVER_TYPE_OPTIONS,
  INVESTMENT_STYLE_OPTIONS,
  PENSION_TYPE_OPTIONS,
  PREMIUM_FREQUENCY_OPTIONS,
  PROTECTION_TYPE_OPTIONS,
} from '../../types/proposal';

export type BadgeColor =
  | 'grey'
  | 'orange'
  | 'yellow'
  | 'green'
  | 'red'
  | 'blue'
  | 'teal'
  | 'neutral'
  | 'purple';

export interface DisplayRow {
  label: string;
  value: string;
}

export interface FundLineRow {
  fundName: string;
  fundReference?: string;
  allocation?: string;
  amount?: string;
}

type Option = { value: string; label: string };

/** Title-case a SNAKE_CASE or lowercase token, e.g. EXECUTIVE_PENSION -> "Executive Pension". */
export function humanize(value: string): string {
  return value
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function labelFrom(options: ReadonlyArray<Option>, value: unknown): string | undefined {
  if (value == null || value === '') return undefined;
  return options.find((o) => o.value === value)?.label ?? humanize(String(value));
}

/** Format a money amount with its currency, e.g. (1000,'EUR') -> "€1,000.00". Undefined when empty. */
export function formatCurrency(amount: unknown, currency?: unknown): string | undefined {
  if (amount == null || amount === '') return undefined;
  const n = Number(amount);
  if (Number.isNaN(n)) return String(amount);
  const code = (typeof currency === 'string' && currency) || 'EUR';
  try {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: code,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `${code} ${n.toLocaleString()}`;
  }
}

function amountWithFrequency(
  amount: unknown,
  currency: unknown,
  freq: unknown,
  freqOptions: ReadonlyArray<Option>,
): string | undefined {
  const money = formatCurrency(amount, currency);
  if (!money) return undefined;
  const freqLabel = labelFrom(freqOptions, freq);
  return freqLabel ? `${money} · ${freqLabel}` : money;
}

/**
 * Build ordered, human-labelled, formatted scalar rows for a proposal's detail
 * view. Empty optional fields are omitted (not shown as "—"). Type-specific so
 * the advisor/client sees real labels and values, not raw field names.
 */
export function proposalScalarRows(
  proposalType: string,
  details: Record<string, unknown> | null | undefined,
): DisplayRow[] {
  const d = details ?? {};
  const rows: DisplayRow[] = [];
  const push = (label: string, value: string | undefined | null) => {
    if (value != null && value !== '') rows.push({ label, value });
  };

  switch (proposalType) {
    case 'PENSION':
      push('Pension type', labelFrom(PENSION_TYPE_OPTIONS, d.pensionType));
      push(
        'Recommended contribution',
        amountWithFrequency(
          d.recommendedContributionAmount,
          d.currencyCode,
          d.contributionFrequency,
          CONTRIBUTION_FREQUENCY_OPTIONS,
        ),
      );
      push('Normal retirement age', d.normalRetirementAge != null ? String(d.normalRetirementAge) : undefined);
      push('Projected value', formatCurrency(d.projectedValue, d.currencyCode));
      push('Annual management charge', d.amcPercent != null ? `${d.amcPercent}%` : undefined);
      push('Rationale', d.rationale ? String(d.rationale) : undefined);
      break;
    case 'PROTECTION':
      push('Protection type', labelFrom(PROTECTION_TYPE_OPTIONS, d.protectionType));
      push('Cover type', labelFrom(COVER_TYPE_OPTIONS, d.coverType));
      push('Sum assured', formatCurrency(d.sumAssured, d.currencyCode));
      push('Term', d.termYears != null ? `${d.termYears} years` : undefined);
      push(
        'Premium',
        amountWithFrequency(d.premiumAmount, d.currencyCode, d.premiumFrequency, PREMIUM_FREQUENCY_OPTIONS),
      );
      push('Rationale', d.rationale ? String(d.rationale) : undefined);
      break;
    case 'FUND_INVESTMENT':
      push('Total investment', formatCurrency(d.totalInvestmentAmount, d.currencyCode));
      push('Investment style', labelFrom(INVESTMENT_STYLE_OPTIONS, d.investmentStyle));
      push('Contribution frequency', labelFrom(CONTRIBUTION_FREQUENCY_OPTIONS, d.contributionFrequency));
      push('Rationale', d.rationale ? String(d.rationale) : undefined);
      break;
    default:
      // Unknown/future type: humanise scalar keys, skip nulls + nested objects/arrays.
      Object.entries(d).forEach(([k, v]) => {
        if (v != null && typeof v !== 'object') push(humanize(k), String(v));
      });
  }
  return rows;
}

/** Fund allocation lines for a FUND_INVESTMENT proposal (empty for other types). */
export function fundLineRows(details: Record<string, unknown> | null | undefined): FundLineRow[] {
  const d = details ?? {};
  const lines = Array.isArray(d.lines) ? (d.lines as Array<Record<string, unknown>>) : [];
  return lines.map((l) => ({
    fundName: String(l.fundName ?? ''),
    fundReference: l.fundReference ? String(l.fundReference) : undefined,
    allocation: l.allocationPercent != null ? `${l.allocationPercent}%` : undefined,
    amount: formatCurrency(l.amount, d.currencyCode),
  }));
}

/** Status → display label + Badge colour. */
export const STATUS_DISPLAY: Record<string, { label: string; color: BadgeColor }> = {
  DRAFT: { label: 'Draft', color: 'grey' },
  SENT: { label: 'Sent', color: 'blue' },
  VIEWED: { label: 'Viewed', color: 'blue' },
  PENDING_SIGNATURE: { label: 'Pending Signature', color: 'yellow' },
  ACCEPTED: { label: 'Accepted', color: 'green' },
  REJECTED: { label: 'Rejected', color: 'red' },
  EXPIRED: { label: 'Expired', color: 'grey' },
  WITHDRAWN: { label: 'Withdrawn', color: 'grey' },
};

export function statusDisplay(status: string): { label: string; color: BadgeColor } {
  return STATUS_DISPLAY[status] ?? { label: humanize(status), color: 'neutral' };
}
